import React, { useState } from 'react';
import { FolderIcon, DotsThreeVerticalIcon, PlusIcon, FilmSlateIcon, Image as ImageIcon, TrashIcon, CalendarIcon, PencilSimpleIcon, XIcon, SignOutIcon, UserCircleIcon, EyeIcon, PencilIcon } from '@phosphor-icons/react';
import { ProjectService } from '../lib/projectService';
import { Project } from '../types';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { ProjectWithOwner } from '../lib/projectMemberService';

interface ProjectsPageProps {
    onNavigate: (view: string, projectId?: string) => void;
}

export const ProjectsPage: React.FC<ProjectsPageProps> = ({ onNavigate }) => {
    const { user } = useAuth();
    const { projects, setProjects, refreshProjects, joinedProjects, leaveProject, refreshJoinedProjects, setSelectedProjectId, selectedProjectId } = useProject();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newProjectType, setNewProjectType] = useState<'General' | 'Screenplay' | 'Novel' | 'Poetry & Lyrics'>('Screenplay');
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; projectId: string | null }>({
        isOpen: false,
        projectId: null
    });
    const [deleteConfirmation, setDeleteConfirmation] = useState('');

    // Leave Project State (for joined projects)
    const [leaveModal, setLeaveModal] = useState<{ isOpen: boolean; project: ProjectWithOwner | null }>({
        isOpen: false,
        project: null
    });
    const [isLeaving, setIsLeaving] = useState(false);

    // Edit State
    const [editModal, setEditModal] = useState<{ isOpen: boolean; projectId: string | null; title: string; type: 'General' | 'Screenplay' | 'Novel' | 'Poetry & Lyrics' }>({
        isOpen: false,
        projectId: null,
        title: '',
        type: 'Screenplay'
    });

    // Projects are loaded from context, no need for local loadProjects

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTitle.trim()) return;

        const project = await ProjectService.createProject(newTitle, undefined, newProjectType);
        if (project) {
            setProjects([project, ...projects]);
            setNewTitle('');
            setNewProjectType('Screenplay');
            setShowCreateModal(false);
        }
    };

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setDeleteModal({ isOpen: true, projectId: id });
        setDeleteConfirmation('');
    };

    const confirmDelete = async () => {
        if (!deleteModal.projectId) return;

        await ProjectService.deleteProject(deleteModal.projectId);

        setDeleteModal({ isOpen: false, projectId: null });
        setDeleteConfirmation('');
        refreshProjects();
    };

    const handleEditClick = (e: React.MouseEvent, project: Project) => {
        e.stopPropagation();
        setEditModal({
            isOpen: true,
            projectId: project.id,
            title: project.title,
            type: project.project_type || 'Screenplay'
        });
    };

    // Leave Project Handlers (for joined projects)
    const handleLeaveClick = (e: React.MouseEvent, project: ProjectWithOwner) => {
        e.stopPropagation();
        setLeaveModal({ isOpen: true, project });
    };

    const confirmLeave = async () => {
        if (!leaveModal.project) return;

        setIsLeaving(true);
        const success = await leaveProject(leaveModal.project.id);
        setIsLeaving(false);

        if (success) {
            setLeaveModal({ isOpen: false, project: null });
            refreshJoinedProjects();
        }
    };

    const handleUpdateProject = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editModal.projectId || !editModal.title.trim()) return;

        await ProjectService.updateProject(editModal.projectId, {
            title: editModal.title,
            project_type: editModal.type
        });

        setEditModal({ isOpen: false, projectId: null, title: '', type: 'Screenplay' });
        refreshProjects();
    };

    return (
        <div className="flex-1 h-screen overflow-y-auto bg-onyx relative z-10 px-10 py-10 w-full animate-in fade-in duration-500">
            <div className="max-w-[1400px] mx-auto">
                {/* Header */}
                <div className="mb-8 border-b border-white/5 pb-6 flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-heading font-bold text-white mb-2">Projects</h1>
                        <p className="text-zinc-500 text-sm">Organize and manage your stories and storyboards.</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider rounded-lg hover:bg-zinc-200 transition-colors shadow-lg shadow-white/10"
                    >
                        <PlusIcon className="w-4 h-4" />
                        New Project
                    </button>
                </div>

                {/* MY PROJECTS Section */}
                <div className="mb-12">
                    <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <FolderIcon className="w-5 h-5 text-indigo-400" />
                        My Projects
                        <span className="text-xs text-zinc-500 font-normal">({projects.length})</span>
                    </h2>
                    <div className="flex flex-wrap gap-8">
                        {/* New Project Poster */}
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="group relative w-[320px] aspect-square rounded-xl border-2 border-dashed border-zinc-800 hover:border-zinc-700 bg-zinc-900/20 hover:bg-zinc-900/40 transition-all flex flex-col items-center justify-center gap-4 overflow-hidden flex-shrink-0"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-50" />
                            <div className="w-16 h-16 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center group-hover:scale-110 transition-transform z-10 shadow-2xl">
                                <PlusIcon className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors" />
                            </div>
                            <span className="text-sm font-bold text-zinc-500 uppercase tracking-widest z-10 group-hover:text-zinc-300">New Story</span>
                        </button>

                        {projects.map((project) => {
                            // Find the most recent image across all scenes
                            let coverImage = null;
                            if (project.scenes) {
                                const allShots = project.scenes.flatMap(s => s.shots || []);
                                const sortedShots = allShots.filter(s => s.image_url).reverse();
                                if (sortedShots.length > 0) coverImage = sortedShots[0].image_url;
                            }

                            // Determine Author Name
                            const authorName = project.title_page?.author || user?.name || "Unknown Author";

                            const isSelected = selectedProjectId === project.id;

                            return (
                                <div
                                    key={project.id}
                                    onClick={() => setSelectedProjectId(isSelected ? null : project.id)}
                                    className={`group relative w-[320px] aspect-square bg-onyx rounded-xl overflow-hidden shadow-2xl hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all duration-500 hover:-translate-y-2 select-none flex-shrink-0 cursor-pointer ${
                                        isSelected
                                            ? 'border-2 border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.3)]'
                                            : 'border border-white/5'
                                    }`}
                                >

                                    {/* Background Image (Cover) */}
                                    {coverImage ? (
                                        <div className="absolute inset-0">
                                            <img
                                                src={coverImage}
                                                alt={project.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-carbon/20 group-hover:bg-transparent transition-colors duration-500" />
                                        </div>
                                    ) : (
                                        <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                                            <FolderIcon className="w-16 h-16 text-zinc-700/50" />
                                        </div>
                                    )}

                                    {/* Cinematic Grading / Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                                    {/* Content Layer */}
                                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                                        {/* Top Actions (Hidden by default, show on hover) */}
                                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-[-10px] group-hover:translate-y-0 flex gap-2">
                                            <button
                                                onClick={(e) => handleEditClick(e, project)}
                                                className="p-2 bg-carbon/50 hover:bg-white/20 backdrop-blur-md rounded-full text-white transition-colors border border-white/10"
                                                title="Edit Project"
                                            >
                                                <PencilSimpleIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(e, project.id)}
                                                className="p-2 bg-carbon/50 hover:bg-red-500/80 backdrop-blur-md rounded-full text-white transition-colors border border-white/10"
                                                title="Delete Project"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Typography */}
                                        <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                                            <h3 className="text-2xl font-heading font-black text-white uppercase leading-[0.9] tracking-tight mb-2 line-clamp-3 drop-shadow-lg font-serif">
                                                {project.title}
                                            </h3>

                                            <div className="h-px w-12 bg-white/30 mb-3" />

                                            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium mb-1 drop-shadow-md">
                                                Written by
                                            </p>
                                            <p className="text-xs font-bold text-zinc-200 uppercase tracking-wider drop-shadow-md">
                                                {authorName}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* JOINED PROJECTS Section */}
                {joinedProjects.length > 0 && (
                    <div>
                        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <UserCircleIcon className="w-5 h-5 text-emerald-400" />
                            Joined Projects
                            <span className="text-xs text-zinc-500 font-normal">({joinedProjects.length})</span>
                        </h2>
                        <div className="flex flex-wrap gap-8">
                            {joinedProjects.map((project) => {
                                // Find the most recent image across all scenes
                                let coverImage = null;
                                if (project.scenes) {
                                    const allShots = project.scenes.flatMap(s => s.shots || []);
                                    const sortedShots = allShots.filter(s => s.image_url).reverse();
                                    if (sortedShots.length > 0) coverImage = sortedShots[0].image_url;
                                }

                                // Owner info
                                const ownerName = project.owner?.username || 'Unknown';

                                const isSelected = selectedProjectId === project.id;

                                return (
                                    <div
                                        key={project.id}
                                        onClick={() => setSelectedProjectId(isSelected ? null : project.id)}
                                        className={`group relative w-[320px] aspect-square bg-onyx rounded-xl overflow-hidden shadow-2xl hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all duration-500 hover:-translate-y-2 select-none flex-shrink-0 cursor-pointer ${
                                            isSelected
                                                ? 'border-2 border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.3)]'
                                                : 'border border-white/5'
                                        }`}
                                    >

                                        {/* Role Badge */}
                                        <div className="absolute top-4 left-4 z-20">
                                            <span className={`
                                                px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 backdrop-blur-md border
                                                ${project.role === 'editor'
                                                    ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                                                    : 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30'
                                                }
                                            `}>
                                                {project.role === 'editor' ? (
                                                    <PencilIcon className="w-3 h-3" />
                                                ) : (
                                                    <EyeIcon className="w-3 h-3" />
                                                )}
                                                {project.role === 'editor' ? 'Editor' : 'Viewer'}
                                            </span>
                                        </div>

                                        {/* Background Image (Cover) */}
                                        {coverImage ? (
                                            <div className="absolute inset-0">
                                                <img
                                                    src={coverImage}
                                                    alt={project.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                />
                                                <div className="absolute inset-0 bg-carbon/20 group-hover:bg-transparent transition-colors duration-500" />
                                            </div>
                                        ) : (
                                            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                                                <FolderIcon className="w-16 h-16 text-zinc-700/50" />
                                            </div>
                                        )}

                                        {/* Cinematic Grading / Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                                        {/* Content Layer */}
                                        <div className="absolute inset-0 flex flex-col justify-end p-6">
                                            {/* Top Actions - Leave Button */}
                                            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-[-10px] group-hover:translate-y-0 flex gap-2">
                                                <button
                                                    onClick={(e) => handleLeaveClick(e, project)}
                                                    className="p-2 bg-carbon/50 hover:bg-orange-500/80 backdrop-blur-md rounded-full text-white transition-colors border border-white/10"
                                                    title="Leave Project"
                                                >
                                                    <SignOutIcon className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Typography */}
                                            <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                                                <h3 className="text-2xl font-heading font-black text-white uppercase leading-[0.9] tracking-tight mb-2 line-clamp-3 drop-shadow-lg font-serif">
                                                    {project.title}
                                                </h3>

                                                <div className="h-px w-12 bg-white/30 mb-3" />

                                                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium mb-1 drop-shadow-md">
                                                    Owned by
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    {project.owner?.avatar_url ? (
                                                        <img
                                                            src={project.owner.avatar_url}
                                                            alt={ownerName}
                                                            className="w-5 h-5 rounded-full object-cover border border-white/20"
                                                        />
                                                    ) : (
                                                        <UserCircleIcon className="w-5 h-5 text-zinc-400" />
                                                    )}
                                                    <p className="text-xs font-bold text-zinc-200 uppercase tracking-wider drop-shadow-md">
                                                        @{ownerName}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-carbon/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <form
                        onSubmit={handleCreate}
                        className="w-full max-w-md bg-onyx border border-white/10 rounded-2xl p-6 shadow-2xl"
                    >
                        <h2 className="text-xl font-bold text-white mb-1">New Project</h2>
                        <p className="text-zinc-400 text-sm mb-6">Start a new story. You can add scenes and details later.</p>

                        <div className="space-y-5">
                            <div>
                                <label className="text-xs uppercase font-bold text-zinc-500 mb-1.5 block">Project Title</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={newTitle}
                                    onChange={e => setNewTitle(e.target.value)}
                                    placeholder="e.g. The Matrix 4"
                                    className="w-full bg-carbon/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="text-xs uppercase font-bold text-zinc-500 mb-1.5 block">Project Type</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {(['Screenplay', 'Novel'] as const).map((type) => {
                                        const isActive = type === 'Screenplay';
                                        return (
                                            <button
                                                key={type}
                                                type="button"
                                                onClick={() => isActive && setNewProjectType(type)}
                                                disabled={!isActive}
                                                className={`
                                                    px-3 py-2.5 rounded-lg text-xs font-medium border transition-all text-center
                                                    ${!isActive
                                                        ? 'bg-zinc-900/50 text-zinc-600 border-white/5 cursor-not-allowed'
                                                        : newProjectType === type
                                                            ? 'bg-white text-black border-white'
                                                            : 'bg-zinc-900 text-zinc-400 border-white/5 hover:border-white/20 hover:text-zinc-200'}
                                                `}
                                            >
                                                {type}
                                                {!isActive && <span className="ml-1 text-[9px] text-zinc-600">(Soon)</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-2">
                                    Determines the writing format and templates available in the Story editor.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewProjectType('Screenplay');
                                }}
                                className="px-4 py-2 hover:bg-white/5 text-zinc-400 hover:text-white rounded-lg transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!newTitle.trim()}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors text-sm font-bold shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Create Project
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Edit Project Modal */}
            {editModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-carbon/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="w-full max-w-md bg-onyx border border-white/10 rounded-2xl p-6 shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setEditModal(prev => ({ ...prev, isOpen: false }))}
                            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
                        >
                            <XIcon className="w-5 h-5" />
                        </button>

                        <h2 className="text-xl font-bold text-white mb-6">Edit Project</h2>

                        <form onSubmit={handleUpdateProject} className="space-y-6">
                            <div>
                                <label className="text-xs uppercase font-bold text-zinc-500 mb-1.5 block">Project Title</label>
                                <input
                                    type="text"
                                    value={editModal.title}
                                    onChange={e => setEditModal(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full bg-carbon/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none transition-colors"
                                    placeholder="Project Name"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="text-xs uppercase font-bold text-zinc-500 mb-1.5 block">Project Type</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['General', 'Screenplay', 'Novel', 'Poetry & Lyrics'].map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => setEditModal(prev => ({ ...prev, type: type as any }))}
                                            className={`
                                                px-3 py-2 rounded-lg text-xs font-medium border transition-all text-center
                                                ${editModal.type === type
                                                    ? 'bg-white text-black border-white'
                                                    : 'bg-zinc-900 text-zinc-400 border-white/5 hover:border-white/20 hover:text-zinc-200'}
                                            `}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-2">
                                    Determines the writing format and templates available in the Story editor.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setEditModal(prev => ({ ...prev, isOpen: false }))}
                                    className="px-4 py-2 hover:bg-white/5 text-zinc-400 hover:text-white rounded-lg transition-colors text-sm font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!editModal.title.trim()}
                                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors text-sm font-bold shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-carbon/90 backdrop-blur-md p-4 animate-in fade-in duration-200">
                    <div className="w-full max-w-md bg-onyx border border-red-500/20 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-red-500/50" />

                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                <TrashIcon className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Delete Project</h2>
                                <p className="text-zinc-500 text-sm">This action cannot be undone.</p>
                            </div>
                        </div>

                        <p className="text-zinc-300 text-sm mb-6 leading-relaxed">
                            To confirm deletion, please type <span className="text-white font-mono bg-white/10 px-1.5 py-0.5 rounded">delete</span> below.
                        </p>

                        <div className="space-y-4">
                            <input
                                autoFocus
                                type="text"
                                value={deleteConfirmation}
                                onChange={e => setDeleteConfirmation(e.target.value)}
                                placeholder="Type 'delete'"
                                className="w-full bg-carbon/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-700 focus:border-red-500/50 focus:outline-none transition-colors font-mono text-sm"
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setDeleteModal({ isOpen: false, projectId: null });
                                    setDeleteConfirmation('');
                                }}
                                className="px-4 py-2 hover:bg-white/5 text-zinc-400 hover:text-white rounded-lg transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.toLowerCase() !== 'delete'}
                                className="px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors text-sm font-bold shadow-lg shadow-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500 disabled:shadow-none"
                            >
                                Delete Forever
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Leave Project Confirmation Modal */}
            {leaveModal.isOpen && leaveModal.project && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-carbon/90 backdrop-blur-md p-4 animate-in fade-in duration-200">
                    <div className="w-full max-w-md bg-onyx border border-orange-500/20 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-orange-500/50" />

                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                                <SignOutIcon className="w-5 h-5 text-orange-500" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Leave Project</h2>
                                <p className="text-zinc-500 text-sm">You can be re-invited later.</p>
                            </div>
                        </div>

                        <p className="text-zinc-300 text-sm mb-2 leading-relaxed">
                            Are you sure you want to leave <span className="text-white font-semibold">"{leaveModal.project.title}"</span>?
                        </p>
                        <p className="text-zinc-500 text-xs mb-6">
                            You will lose access to all project content until the owner invites you again.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setLeaveModal({ isOpen: false, project: null })}
                                disabled={isLeaving}
                                className="px-4 py-2 hover:bg-white/5 text-zinc-400 hover:text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmLeave}
                                disabled={isLeaving}
                                className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-sm font-bold shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isLeaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Leaving...
                                    </>
                                ) : (
                                    <>
                                        <SignOutIcon className="w-4 h-4" />
                                        Leave Project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
